import { useEffect, useId, useRef, useState, type ReactNode, type RefObject } from "react";
import type { WhatsAppConnectionView, WhatsAppMetaSignup, WhatsAppMetaSignupBody, WhatsAppSenderPerson } from "@gioia365/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ApiRequestError } from "../../api/http";
import { Button } from "../../design/components/Button";
import { Icon } from "../../design/components/Icon";
import { Reason } from "../../design/components/Reason";
import { StatusPill } from "../../design/components/StatusPill";
import { messageForError } from "../../i18n/errors";
import { formatNumericDateTime } from "../../i18n/format";
import { useLocale, useT, type Translate } from "../../i18n/locale";
import { withLink } from "../../i18n/with-link";
import { useAccess } from "../../session/access";
import type { TabContext } from "../../session/tab-context";
import { openMetaSignupWindow, preloadFacebookSdk } from "../../whatsapp/meta-signup";
import { failureReasonKey, signupFailureText } from "../../whatsapp/meta-texts";
import { useRegisterOnMeta, whatsAppSendersQuery } from "../../whatsapp/settings-api";
import type { Feedback } from "./draft-dialogs";

// Il WhatsApp ufficiale di Meta nelle Impostazioni (piano M6, R2, Task 7b; D7, D9; questioni 408, 409, 412 e 416; mock-up
// `whatsapp.html`, `team.html` e `mockup.js`, `bindWaSetup` e `initWaChannel`):
// - «Registra su Meta e salva» nella finestra «Imposta» (`useMetaRegistration`). L'SDK di Facebook si carica appena la finestra mostra
//   il WhatsApp ufficiale, così la finestra di Meta si apre dentro il clic e il browser non la blocca (decisione del controller del 6
//   ottobre 2026). Due strade, dal collegamento dell'azienda (`metaSignup.connection`):
//   · azienda già `connected`: prima `POST /api/whatsapp/meta-signup` senza codice (il server salta la finestra di Meta se il numero
//     è già nel WhatsApp Business Account dell'azienda, questione 412); con `409 whatsapp_meta_window_required` la finestra di Meta;
//   · nessuna registrazione ancora, `not_connected` o `failed`: la finestra di Meta subito, dentro il clic (il server la chiederebbe
//     comunque), poi la chiamata con il codice e il WhatsApp Business Account (il codice vale 30 secondi).
//   Prima di aprire la finestra, un Business Manager che ha già tutti i numeri che può registrare lo dice il campo del numero con le
//   parole del mock-up: Meta rifiuterebbe comunque (un blocco chiaro, E1). Ogni rifiuto ha il suo testo, mai un blocco muto;
// - lo stato del WhatsApp ufficiale della persona nella finestra (`MetaSenderStatus`): «Registrato su Meta», in corso, non riuscito con
//   il passo e il motivo e «Riprova», la finestra di Meta chiusa prima della fine con «Apri la finestra di Meta» (la riserva se il
//   browser l'ha bloccata lo stesso: un clic che la apre subito). «Riprova» manda prima la persona, il numero e la versione, senza
//   codice (il server riprende dal passo dopo il gettone, o chiede la finestra); soltanto dopo uno scambio del codice non riuscito,
//   con l'azienda non `connected` (il gettone non c'è), apre subito la finestra di Meta. Mentre si aspetta Meta la finestra «Imposta»
//   si chiude («Annulla», la X, Esc): l'attesa si abbandona e una risposta di Meta che arriva dopo non va al server (`abandon`); un
//   SDK che non arriva o non parte in tempo porta al motivo con «Apri la finestra di Meta» (`meta-signup.ts`). Non si chiude soltanto
//   mentre il server lavora (`registering`: una chiamata breve, che non si lascia a metà);
// - il riquadro «Business Manager di Meta» della pagina «Invio dei WhatsApp» (`MetaBusinessPanel`, questione 416), con i modelli
//   approvati da Meta (Task 8b).
// La versione che la registrazione porta è quella della finestra «Imposta» (`baseVersionRef`): dopo un passo non riuscito il server dice
// quella nuova (`details.version` del `502`), altrimenti si rilegge l'elenco delle persone.

/** Nessuna registrazione ancora: nessun collegamento, o una riga nata soltanto dai numeri ammessi (mai «in corso»). */
export function noRegistrationYet(connection: WhatsAppConnectionView | null): boolean {
  return connection === null || (connection.state === "not_connected" && connection.setupStep === "none");
}

/** Quanti numeri ha il Business Manager quando sono già al tetto (2, o 20 dopo la verifica); `null` se c'è posto o non si sa. */
export function numbersAtCap(connection: WhatsAppConnectionView | null): number | null {
  if (connection === null || connection.registeredNumbers === null) return null;
  return connection.registeredNumbers >= connection.numberCap ? connection.registeredNumbers : null;
}

/** Dove sta la registrazione nella finestra «Imposta». */
export type MetaPhase =
  | { kind: "idle" }
  | { kind: "registering" }
  | { kind: "window" }
  | { kind: "failed"; step: unknown; code: string | null }
  | { kind: "window_closed"; reason: "cancelled" | "incomplete" | "error" | "sdk_unavailable" };

/** Un rifiuto da dire: sul campo del numero o in fondo alla finestra. */
export type MetaFeedback = Feedback & { field: "number" | "form" };

type RegistrationOptions = {
  tab: TabContext;
  /** La finestra «Imposta» mostra il WhatsApp ufficiale: l'SDK di Facebook si carica adesso. */
  showing: boolean;
  /** La persona come la segue la finestra (la sua riga nell'elenco riletto). */
  person: WhatsAppSenderPerson;
  /** Le persone dell'azienda (`null` finché non sono arrivate): un numero di un collega si dice prima di aprire la finestra di Meta. */
  people: readonly WhatsAppSenderPerson[] | null;
  /** Ciò che serve per la finestra di Meta (`metaSignup` dei WhatsApp dell'azienda), `null` finché non è arrivato. */
  signup: WhatsAppMetaSignup | null;
  /** La versione del WhatsApp della persona che la finestra porta (`null`: non ne ha uno). */
  baseVersionRef: RefObject<number | null>;
  onFeedback: (feedback: MetaFeedback | null) => void;
  /** Registrato: il testo di «Cosa cambia:» è della finestra. */
  onSaved: () => void;
};

const isCode = (error: unknown, code: string): error is ApiRequestError => error instanceof ApiRequestError && error.code === code;

/** «Registra su Meta e salva», «Riprova» e «Apri la finestra di Meta» della finestra «Imposta». */
export function useMetaRegistration({ tab, showing, person, people, signup, baseVersionRef, onFeedback, onSaved }: RegistrationOptions) {
  const { locale } = useLocale();
  const queryClient = useQueryClient();
  // L'attesa della finestra di Meta in corso, da abbandonare se la finestra «Imposta» si chiude.
  const waitingRef = useRef<AbortController | null>(null);
  useEffect(() => () => waitingRef.current?.abort(), []);
  const register = useRegisterOnMeta(tab);
  const [phase, setPhase] = useState<MetaPhase>({ kind: "idle" });
  const busy = phase.kind === "registering" || phase.kind === "window";

  // L'SDK di Facebook in anticipo: un SDK che non arriva si richiede quando la finestra di Meta si apre.
  const appId = signup?.available ? signup.appId : null;
  const graphVersion = signup?.available ? signup.graphVersion : null;
  useEffect(() => {
    if (!showing || appId === null || graphVersion === null) return;
    preloadFacebookSdk({ appId, graphVersion, locale }).catch(() => {});
  }, [showing, appId, graphVersion, locale]);

  function bodyOf(e164: string, fromWindow?: { code: string; wabaId: string }): WhatsAppMetaSignupBody {
    const version = baseVersionRef.current;
    return { membershipId: person.membershipId, e164, ...(version === null ? {} : { expectedVersion: version }), ...fromWindow };
  }

  /** La versione di adesso dopo un passo non riuscito: quella del `502`, altrimenti quella dell'elenco riletto. */
  async function adoptVersionAfterFailure(error: ApiRequestError) {
    const told = error.details?.version;
    if (typeof told === "number" || told === null) {
      baseVersionRef.current = told;
      return;
    }
    try {
      const fresh = await queryClient.fetchQuery(whatsAppSendersQuery(tab));
      baseVersionRef.current = fresh.people.find((candidate) => candidate.membershipId === person.membershipId)?.sender?.version ?? null;
    } catch {
      // L'elenco non si rilegge: «Riprova» porta la versione di prima e il server, se è cambiata, risponde `409 version_conflict`.
    }
  }

  async function refused(error: unknown) {
    if (isCode(error, "whatsapp_signup_failed")) {
      await adoptVersionAfterFailure(error);
      const code = error.details?.failureCode;
      setPhase({ kind: "failed", step: error.details?.step, code: typeof code === "string" ? code : null });
      return;
    }
    setPhase({ kind: "idle" });
    if (isCode(error, "whatsapp_number_taken")) {
      const holder = error.details?.holderDisplayName;
      onFeedback({
        field: "number",
        text: (tr) => (typeof holder === "string" && holder.trim() !== "" ? tr("whatsappSendingSettings.dialog.numberTaken", { name: holder }) : messageForError(tr, error)),
      });
    } else if (isCode(error, "version_conflict")) {
      const current = error.details?.version;
      baseVersionRef.current = typeof current === "number" ? current : current === null ? null : (person.sender?.version ?? null);
      const name = person.displayName;
      onFeedback({ field: "form", text: (tr) => tr("whatsappSendingSettings.dialog.conflict", { name }) });
    } else if (isCode(error, "whatsapp_number_cap_reached")) {
      const told = error.details?.registeredNumbers;
      const count = typeof told === "number" ? told : (signup?.connection?.registeredNumbers ?? signup?.connection?.numberCap ?? 2);
      onFeedback({ field: "number", text: (tr) => tr("whatsappSendingSettings.meta.capReached", { count }) });
    } else if (isCode(error, "whatsapp_number_mismatch")) {
      onFeedback({ field: "number", text: (tr) => messageForError(tr, error) });
    } else {
      onFeedback({ field: "form", text: (tr) => messageForError(tr, error) });
    }
  }

  /** Una chiamata della registrazione; `window` se il server chiede la finestra di Meta (soltanto senza codice). */
  async function send(e164: string, fromWindow?: { code: string; wabaId: string }): Promise<"window" | "done"> {
    setPhase({ kind: "registering" });
    try {
      await register.mutateAsync(bodyOf(e164, fromWindow));
      setPhase({ kind: "idle" });
      onSaved();
    } catch (error) {
      if (fromWindow === undefined && isCode(error, "whatsapp_meta_window_required")) return "window";
      await refused(error);
    }
    return "done";
  }

  /** La finestra di Meta, poi la registrazione con il codice appena arriva. */
  async function openWindow(e164: string) {
    const { appId, configId, graphVersion } = signup ?? { appId: null, configId: null, graphVersion: null };
    if (signup?.available !== true || appId === null || configId === null || graphVersion === null) {
      setPhase({ kind: "idle" });
      onFeedback({ field: "form", text: (tr) => tr("errors.whatsapp_meta_not_configured") });
      return;
    }
    onFeedback(null);
    setPhase({ kind: "window" });
    const waiting = new AbortController();
    waitingRef.current = waiting;
    const outcome = await openMetaSignupWindow({ appId, configId, graphVersion, locale }, waiting.signal);
    if (waitingRef.current === waiting) waitingRef.current = null;
    // Abbandonata: nessuna chiamata al server, anche se Meta ha risposto.
    if (waiting.signal.aborted) return;
    if (outcome.kind === "finished") {
      await send(e164, { code: outcome.code, wabaId: outcome.wabaId });
      return;
    }
    setPhase({ kind: "window_closed", reason: outcome.kind === "cancelled" ? "cancelled" : outcome.reason });
  }

  /**
   * «Registra su Meta e salva»: con l'azienda già `connected` prima la chiamata senza codice, e la finestra di Meta soltanto se il
   * server la chiede; altrimenti la finestra subito, dentro il clic (nessuna attesa prima di `FB.login`), dopo i controlli che il
   * browser sa fare da sé (il numero di un collega attivo, come il mock-up). «Riprova» (`resume`) fa prima la chiamata senza codice:
   * il server riprende dal passo dopo il gettone, o chiede la finestra; soltanto dopo uno scambio del codice non riuscito, senza
   * l'azienda `connected` (il gettone non c'è), apre subito la finestra.
   */
  async function start(e164: string, { resume = false }: { resume?: boolean } = {}) {
    if (busy) return;
    onFeedback(null);
    const connected = signup?.connection?.state === "connected";
    const tokenMissing = phase.kind === "failed" && phase.step === "token";
    if ((connected || (resume && !tokenMissing)) && (await send(e164)) !== "window") return;
    if (!connected) {
      const holder = people?.find((other) => other.membershipId !== person.membershipId && other.status === "active" && other.sender?.e164 === e164);
      if (holder) {
        const name = holder.displayName;
        onFeedback({ field: "number", text: (tr) => tr("whatsappSendingSettings.dialog.numberTaken", { name }) });
        return;
      }
    }
    // Un numero che la persona ha già sul WhatsApp ufficiale è fra quelli registrati: il tetto non lo blocca (come il mock-up).
    const own = person.sender?.channel === "meta" && person.sender.e164 === e164;
    const atCap = own ? null : numbersAtCap(signup?.connection ?? null);
    if (atCap !== null) {
      setPhase({ kind: "idle" });
      onFeedback({ field: "number", text: (tr) => tr("whatsappSendingSettings.meta.capReached", { count: atCap }) });
      return;
    }
    await openWindow(e164);
  }

  /** «Apri la finestra di Meta», dopo una finestra chiusa prima della fine: si apre subito, dentro il clic. */
  async function reopen(e164: string) {
    if (busy) return;
    await openWindow(e164);
  }

  /** La finestra «Imposta» si chiude mentre si aspetta Meta: l'attesa si abbandona. */
  function abandon() {
    waitingRef.current?.abort();
    waitingRef.current = null;
  }

  // Mentre il server lavora la finestra «Imposta» non si chiude; mentre si aspetta Meta sì (`abandon`).
  const closable = phase.kind !== "registering";
  return { phase, busy, closable, start, reopen, abandon };
}

/** «Registrazione su Meta:» nella finestra «Imposta», con quanti numeri ha il Business Manager quando Meta l'ha detto. */
export function MetaRegistrationNote({ connection }: { connection: WhatsAppConnectionView | null }) {
  const t = useT();
  const count = connection?.registeredNumbers ?? null;
  return (
    <p className="g-wa-auto">
      <Icon name="info" />
      <span>
        <strong>{t("whatsappSendingSettings.meta.registrationTitle")}</strong> {t("whatsappSendingSettings.meta.registrationText")}
        {count === null || connection === null ? null : (
          <>
            {" "}
            {t("whatsappSendingSettings.meta.registeredCount", { count, cap: connection.numberCap })}
          </>
        )}
      </span>
    </p>
  );
}

type StatusProps = {
  person: WhatsAppSenderPerson;
  phase: MetaPhase;
  busy: boolean;
  /** Lo stato di adesso di un WhatsApp ufficiale non in errore (la pillola di «Invio dei WhatsApp»). */
  current: ReactNode;
  onRetry: () => void;
  onReopen: () => void;
};

/** Il motivo per cui la finestra di Meta non è finita. */
function windowClosedText(t: Translate, reason: Extract<MetaPhase, { kind: "window_closed" }>["reason"]): string {
  switch (reason) {
    case "cancelled":
      return t("whatsappSendingSettings.meta.windowClosed.cancelled");
    case "incomplete":
      return t("whatsappSendingSettings.meta.windowClosed.incomplete");
    case "error":
      return t("whatsappSendingSettings.meta.windowClosed.error");
    case "sdk_unavailable":
      return t("whatsappSendingSettings.meta.windowClosed.sdkUnavailable");
  }
}

/**
 * Lo stato della registrazione su Meta nella finestra «Imposta»: mentre lavora, in corso (annunciato); non riuscita, il passo e il
 * motivo con «Riprova»; la finestra di Meta chiusa prima della fine, il perché con «Apri la finestra di Meta». Altrimenti, per chi ha
 * già il WhatsApp ufficiale, com'è adesso: «Registrato su Meta», non pronto con il motivo, o non riuscito con «Riprova».
 */
export function MetaSenderStatus({ person, phase, busy, current, onRetry, onReopen }: StatusProps) {
  const t = useT();
  const ids = useId();
  const sender = person.sender?.channel === "meta" ? person.sender : null;
  const retryLabel = t("whatsappSendingSettings.meta.retryNamed", { name: person.displayName });

  let body: ReactNode = null;
  if (phase.kind === "failed" || (phase.kind === "idle" && sender?.state === "failed")) {
    const text = phase.kind === "failed" ? signupFailureText(t, phase.step, phase.code) : signupFailureText(t, null, sender?.failureCode ?? null);
    body = (
      <div className="g-wa-meta-status__problem">
        <Reason tone="warning" id={`${ids}-why`}>
          {text}
        </Reason>
        <Button onClick={onRetry} disabled={busy} aria-label={retryLabel} aria-describedby={`${ids}-why`}>
          {t("whatsappSendingSettings.meta.retry")}
        </Button>
      </div>
    );
  } else if (phase.kind === "window_closed") {
    body = (
      <div className="g-wa-meta-status__problem">
        <Reason tone="warning" id={`${ids}-why`}>
          {windowClosedText(t, phase.reason)}
        </Reason>
        <Button onClick={onReopen} disabled={busy} aria-describedby={`${ids}-why`}>
          {t("whatsappSendingSettings.meta.openWindow")}
        </Button>
      </div>
    );
  } else if (phase.kind === "idle" && sender !== null) {
    body = <p className="g-wa-meta-status__now">{current}</p>;
  }

  // Lo stato in corso sta sempre nella pagina e vuoto finché non serve: uno screen reader annuncia soltanto ciò che vi compare.
  let progress: string | null = null;
  if (phase.kind === "registering") progress = t("whatsappSendingSettings.meta.registering");
  else if (phase.kind === "window") progress = t("whatsappSendingSettings.meta.windowOpen");
  return (
    <div className="g-wa-meta-status">
      <p className="g-wa-muted" role="status">
        {progress}
      </p>
      {body}
    </div>
  );
}

/** Quanti dei modelli dell'azienda Meta ha approvato con il testo di adesso, su quanti. */
export type ApprovedTemplates = { approved: number; total: number };

type BusinessPanelProps = {
  connection: WhatsAppConnectionView | null;
  /** I modelli approvati da Meta, `null` finché i modelli non si sono letti (la riga non c'è). */
  templates: ApprovedTemplates | null;
};

/**
 * Il riquadro «Business Manager di Meta» della pagina «Invio dei WhatsApp» (questione 416), con l'azienda che usa l'ufficiale: il
 * nome e i numeri come li ha detti Meta, «Quante persone al giorno» e «Modelli approvati da Meta: N di 6» con il collegamento a
 * «Modelli WhatsApp» (mock-up `initWaChannel`).
 */
export function MetaBusinessPanel({ connection, templates }: BusinessPanelProps) {
  const t = useT();
  const titleId = useId();
  const timeZone = useAccess().context.settings.timeZone;

  let status: string | null = null;
  if (noRegistrationYet(connection)) status = t("whatsappSendingSettings.business.none");
  else if (connection?.state === "failed") status = t("whatsappSendingSettings.business.failed", { reason: t(failureReasonKey(connection.setupError)) });
  else if (connection?.state === "not_connected") status = t("whatsappSendingSettings.business.unfinished");
  // Il nome e i numeri soltanto quando Meta li ha detti almeno una volta.
  const known = connection !== null && (connection.businessName !== null || connection.registeredNumbers !== null);

  return (
    <section className="g-wa-section" aria-labelledby={titleId}>
      <h2 id={titleId}>{t("whatsappSendingSettings.business.title")}</h2>
      {status === null ? null : <Reason tone={connection?.state === "failed" ? "warning" : "info"}>{status}</Reason>}
      <ul className="g-wa-kinds">
        {known && connection ? (
          <li className="g-wa-business">
            <span className="g-wa-mode__head">
              <strong className="g-wa-business__name">{connection.businessName ?? t("whatsappSendingSettings.business.nameUnknown")}</strong>
              {connection.businessVerified ? (
                <StatusPill tone="success" icon="check">
                  {t("whatsappSendingSettings.business.verified")}
                </StatusPill>
              ) : null}
            </span>
            <span className="g-wa-mode__text">
              {connection.registeredNumbers === null
                ? t("whatsappSendingSettings.business.numbersUnknown")
                : t("whatsappSendingSettings.business.numbers", { count: connection.registeredNumbers, cap: connection.numberCap })}
            </span>
          </li>
        ) : null}
        <li className="g-wa-business">
          <span className="g-wa-mode__head">
            <strong>{t("whatsappSendingSettings.business.perDayTitle")}</strong>
          </span>
          <span className="g-wa-mode__text">{t("whatsappSendingSettings.business.perDayText")}</span>
        </li>
        {templates ? (
          <li className="g-wa-business">
            <span className="g-wa-mode__head">
              <strong>{t("whatsappSendingSettings.business.templatesApproved", { approved: templates.approved, total: templates.total })}</strong>
            </span>
            <span className="g-wa-mode__text">
              {withLink(t("whatsappSendingSettings.business.templatesText"), <Link to="/impostazioni/modelli-whatsapp">{t("settings.whatsappTemplates")}</Link>)}
            </span>
          </li>
        ) : null}
      </ul>
      {connection?.readAt ? <p className="g-wa-muted">{t("whatsappSendingSettings.business.readAt", { when: formatNumericDateTime(timeZone, connection.readAt) })}</p> : null}
    </section>
  );
}
